/**
 * Таблица дочерних событий для BatchEventPage
 * Переиспользуемый компонент с возможностью выполнения событий
 */
import React from 'react';
import { Table, Button, Space } from 'antd';
import type { BatchChildEvent } from '../../../types/plantingEvents';
import { EventStatusTag } from './EventStatusTag';
import { EventLocationCell } from './EventLocationCell';
import { formatEventDate } from '../utils/eventFormatters';
import { useTranslation } from 'react-i18next';

interface BatchEventTableProps {
  events: BatchChildEvent[];
  loading?: boolean;
  onCompleteEvent: (event: BatchChildEvent) => void;
  canCompleteEvents: boolean;
  selectedEventIds?: string[];
  onSelectionChange?: (selectedIds: string[]) => void;
}

export const BatchEventTable: React.FC<BatchEventTableProps> = ({
  events,
  loading,
  onCompleteEvent,
  canCompleteEvents,
  selectedEventIds = [],
  onSelectionChange,
}) => {
  const { t } = useTranslation('events');
  const columns = [
    {
      title: '#',
      dataIndex: 'batchSequence',
      key: 'batchSequence',
      width: 60,
      render: (num: number) => `#${num}`,
    },
    {
      title: t('batchTable.location'),
      key: 'location',
      render: (record: BatchChildEvent) => (
        <EventLocationCell event={record} showTargetType={false} />
      ),
    },
    {
      title: t('batchTable.targetType'),
      key: 'targetType',
      width: 120,
      render: (record: BatchChildEvent) => (
        <EventLocationCell event={{ ...record }} showTargetType={true} />
      ),
    },
    {
      title: t('common:labels.status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: any) => <EventStatusTag status={status} />,
    },
    {
      title: t('batchTable.actualDate'),
      dataIndex: 'actualDate',
      key: 'actualDate',
      width: 140,
      render: (date: string | null) => formatEventDate(date),
    },
    {
      title: t('common:labels.actions'),
      key: 'actions',
      width: 120,
      render: (record: BatchChildEvent) => (
        <Space>
          {canCompleteEvents &&
            (record.status === 'planned' ||
              record.status === 'pending' ||
              record.status === 'in_progress') && (
              <Button type="primary" size="small" onClick={() => onCompleteEvent(record)}>
                {t('actions.complete')}
              </Button>
            )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      dataSource={events}
      columns={columns}
      rowKey="id"
      loading={loading}
      pagination={false}
      locale={{ emptyText: t('batchTable.empty') }}
      rowSelection={
        onSelectionChange
          ? {
              selectedRowKeys: selectedEventIds,
              onChange: (selectedRowKeys) => {
                onSelectionChange(selectedRowKeys as string[]);
              },
              getCheckboxProps: (record) => ({
                disabled:
                  record.status === 'completed' || record.status === 'cancelled',
              }),
            }
          : undefined
      }
    />
  );
};
